import { Grid, List, ListItemButton, Paper, useMediaQuery, useTheme } from '@mui/material';
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { mb } from '../theme/mbTokens';

const DELETION_TAB_KEY = 'deletion';

type TabsPageProps = {
  tabContent: { [key: string]: ReactNode };
  activeTab?: string;
  onActiveTabChange?: (tab: string) => void;
  guardTabChange?: (nextTab: string) => boolean | Promise<boolean>;
  /** Rendered inside another card: no content card of its own, sized by its content. */
  embedded?: boolean;
};

export const TabsPage = ({
  tabContent,
  activeTab: controlledActiveTab,
  onActiveTabChange,
  guardTabChange,
  embedded = false,
}: TabsPageProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  const [internalActiveTab, setInternalActiveTab] = useState(Object.keys(tabContent)[0]);
  const activeTab = controlledActiveTab ?? internalActiveTab;
  const topGridRef = useRef<HTMLDivElement>(null);

  const commitTab = (next: string) => {
    if (controlledActiveTab === undefined) {
      setInternalActiveTab(next);
    }
    onActiveTabChange?.(next);
    window.location.hash = `#${next}`;
    if (topGridRef.current) {
      topGridRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleTabChange = async (event: React.SyntheticEvent, newValue: string) => {
    if (guardTabChange) {
      const ok = await guardTabChange(newValue);
      if (!ok) return;
    }
    commitTab(newValue);
  };

  useEffect(() => {
    const handleHashChange = async () => {
      const hash = window.location.hash.replace('#', '');
      if (!Object.keys(tabContent).includes(hash) || hash === activeTab) return;
      if (guardTabChange) {
        const ok = await guardTabChange(hash);
        if (!ok) {
          window.location.hash = `#${activeTab}`;
          return;
        }
      }
      commitTab(hash);
    };

    window.addEventListener('hashchange', handleHashChange);

    // Sync tab with hash without scrolling (avoid scroll-to-top on re-renders)
    const hash = window.location.hash.replace('#', '');
    if (Object.keys(tabContent).includes(hash) && hash !== activeTab) {
      if (controlledActiveTab === undefined) {
        setInternalActiveTab(hash);
      }
      onActiveTabChange?.(hash);
    }

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [tabContent, activeTab, guardTabChange, controlledActiveTab, onActiveTabChange]);

  return (
    <Grid
      container
      flexDirection={isSmallScreen ? 'column' : 'row'}
      flexWrap="nowrap"
      sx={{
        height: embedded ? 'auto' : '100%',
        overflow: embedded ? 'visible' : 'hidden',
        gap: mb.layout.pageInset,
      }}>
      <Paper
        component="nav"
        variant="outlined"
        aria-label="language tabs"
        sx={{
          width: isSmallScreen ? '100%' : 200,
          flexShrink: 0,
          p: 1.25,
          borderRadius: '8px',
          alignSelf: isSmallScreen ? 'stretch' : 'flex-start',
          overflowX: isSmallScreen ? 'auto' : undefined,
        }}>
        <List
          disablePadding
          sx={{ display: 'flex', flexDirection: isSmallScreen ? 'row' : 'column', gap: isSmallScreen ? 0.5 : 0.25 }}>
          {Object.keys(tabContent).map((tab) => {
            const isSelected = tab === activeTab;
            const isDanger = tab === DELETION_TAB_KEY;
            return (
              <ListItemButton
                key={`tab-SettingsPageTabs.${tab}`}
                selected={isSelected}
                aria-current={isSelected ? 'page' : undefined}
                onClick={(e) => handleTabChange(e, tab)}
                sx={{
                  minHeight: isSmallScreen ? 40 : 44,
                  px: 1.75,
                  fontSize: 15,
                  ...(isSmallScreen && { whiteSpace: 'nowrap', flexShrink: 0, width: 'auto' }),
                  ...(isDanger && !isSelected && { color: 'error.main' }),
                  ...(isDanger &&
                    !isSmallScreen && {
                      mt: 1,
                      pt: 1,
                      borderTop: '1px solid',
                      borderColor: 'mb.lineSoft',
                      borderRadius: '0 0 6px 6px',
                    }),
                }}>
                {t(`tabs.${tab}`)}
              </ListItemButton>
            );
          })}
        </List>
      </Paper>
      <Paper
        variant="outlined"
        ref={topGridRef}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          borderRadius: '8px',
          p: mb.layout.cardPadding,
          overflowY: embedded ? 'visible' : 'auto',
          ...(embedded && { border: 0, bgcolor: 'transparent', p: 0 }),
        }}>
        {tabContent[activeTab] || null}
      </Paper>
    </Grid>
  );
};
